import { nextTick } from 'vue';

interface ScrollToErrorOptions {
  offset?: number;
  behavior?: ScrollBehavior;
  focus?: boolean;
}

/**
 * Composable for scrolling to the first invalid form field.
 * Detects invalid fields using the `aria-invalid="true"` attribute
 * which is set automatically by form components on validation error.
 */
export function useScrollToError() {
  /**
   * Scrolls to the first invalid field in the given container or document.
   * @param container - Optional container element to search within
   * @param options - Scroll behavior options
   * @returns true if an invalid field was found and scrolled to
   */
  async function scrollToFirstError(
    container?: HTMLElement | null,
    options: ScrollToErrorOptions = {}
  ): Promise<boolean> {
    const { offset = 100, behavior = 'smooth', focus = true } = options;

    await nextTick();

    const searchRoot = container || document;
    const invalidElement = searchRoot.querySelector<HTMLElement>('[aria-invalid="true"]');

    if (!invalidElement) {
      return false;
    }

    const elementRect = invalidElement.getBoundingClientRect();
    const absoluteTop = window.scrollY + elementRect.top - offset;

    window.scrollTo({
      top: absoluteTop,
      behavior,
    });

    if (focus && invalidElement.focus) {
      invalidElement.focus({ preventScroll: true });
    }

    return true;
  }

  return { scrollToFirstError };
}
